import { RenegotiateForm } from '@module/expense/model/RenegotiateForm';
import { QuotationForm } from '@module/quotation/model/QuotationForm';
import { RequestCaseResponse } from '@module/request/model/RequestCaseResponse';
import { RequestForQuotationAndSummaryResponse } from '@module/request/model/RequestForQuotationAndSummaryResponse';
import { RequestsActivitiesExpensesForm } from '@module/request/model/RequestsActivityForm';
import { RequestsAdditionalAgentsForm } from '@module/request/model/RequestsAdditionalAgentsForm';
import {
  QuotationExpenseResponse,
  RequestCaseQuotationsEvaluationResponse
} from '@state';
import { divide } from 'mathjs';
import { mainRestApi } from '../rest-api';
import { RequestForQuotationPricelistAmountsResponse } from '@state/types/RequestForQuotationPricelistAmountsResponse';
import { isBaseQueryError } from '@client';

export const isCtmAmountExpense = (expense: { name: string }) =>
  !!expense.name.match(/ctm amount/i);

export const isCtmBankChargesExpense = (expense: { name: string }) =>
  !!expense.name.match(/ctm bank charges/i);

export const createQuotationExpenseKey = (expense: {
  name: string;
  expenseGroupName?: string | undefined;
  parameters?:
    | {
        parameterId: number;
        optionId: number;
      }[]
    | undefined;
}) =>
  [
    expense.name,
    expense.expenseGroupName,
    (expense.parameters ?? [])
      .slice()
      .sort((a, b) => a.parameterId - b.parameterId)
      .map((a) => a.optionId)
      .join('_')
  ]
    .filter(Boolean)
    .join('_');

export const requestCaseApi = mainRestApi.injectEndpoints({
  endpoints: (build) => ({
    loadRequestCase: build.query<
      RequestCaseResponse,
      { requestCaseId: string | number }
    >({
      query: ({ requestCaseId }) => ({
        url: `/secured/request-case/${requestCaseId}/load`,
        method: 'GET'
      }),
      providesTags: (res, _error, { requestCaseId }) =>
        res
          ? [{ type: 'REQUEST_CASE', id: requestCaseId.toString() }]
          : ['REQUEST_CASE']
    }),
    cancelRequestCase: build.mutation<
      RequestCaseResponse,
      { requestCaseId: string | number }
    >({
      query: ({ requestCaseId }) => ({
        url: `/secured/request-case/${requestCaseId}/cancel`,
        method: 'GET'
      }),
      invalidatesTags: (res, _error, { requestCaseId }) =>
        res
          ? [
              { type: 'REQUEST_CASE', id: requestCaseId.toString() },
              { type: 'REQUEST_CASE_EVALUATE', id: requestCaseId.toString() }
            ]
          : ['REQUEST_CASE', 'REQUEST_CASE_EVALUATE']
    }),
    loadRequestForQuotation: build.query<
      RequestForQuotationAndSummaryResponse,
      { requestForQuotationId: string | number }
    >({
      query: ({ requestForQuotationId }) => ({
        url: `/secured/request-for-quotation/${requestForQuotationId}/load`,
        method: 'GET'
      }),
      providesTags: (res, _error, { requestForQuotationId }) =>
        res
          ? [
              {
                type: 'REQUEST_FOR_QUOTATION',
                id: requestForQuotationId.toString()
              }
            ]
          : ['REQUEST_FOR_QUOTATION']
    }),
    sendRequestForQuotationReminder: build.mutation<
      RequestForQuotationAndSummaryResponse,
      {
        requestForQuotationId: string | number;
      }
    >({
      query: ({ requestForQuotationId }) => ({
        url: '/secured/request-for-quotation/send-reminder',
        method: 'POST',
        data: requestForQuotationId
      }),
      invalidatesTags: (res, _error, {}) =>
        res
          ? [{ type: 'REQUEST_CASE', id: res.requestCaseId }]
          : ['REQUEST_CASE']
    }),
    cancelRequestForQuotation: build.mutation<
      RequestForQuotationAndSummaryResponse,
      {
        requestCaseId: string | number;
        requestForQuotationId: string | number;
      }
    >({
      query: ({ requestCaseId, requestForQuotationId }) => ({
        url: `/secured/request-case/${requestCaseId}/request-for-quotation/${requestForQuotationId}/cancel`,
        method: 'GET'
      }),
      invalidatesTags: (
        res,
        _error,
        { requestCaseId, requestForQuotationId }
      ) =>
        res
          ? [
              { type: 'REQUEST_CASE', id: requestCaseId },
              { type: 'REQUEST_FOR_QUOTATION', id: requestForQuotationId }
            ]
          : ['REQUEST_CASE', 'REQUEST_FOR_QUOTATION']
    }),
    renegotiateQuotation: build.mutation<
      RequestForQuotationAndSummaryResponse,
      RenegotiateForm
    >({
      query: (data) => ({
        url: '/secured/request-for-quotation/renegotiate',
        method: 'POST',
        data
      }),
      invalidatesTags: (res, _error, { requestForQuotationId }) =>
        res
          ? [
              { type: 'REQUEST_CASE', id: res.requestCaseId },
              { type: 'REQUEST_CASE_EVALUATE', id: res.requestCaseId },
              { type: 'REQUEST_FOR_QUOTATION', id: requestForQuotationId }
            ]
          : ['REQUEST_CASE', 'REQUEST_FOR_QUOTATION', 'REQUEST_CASE_EVALUATE']
    }),
    loadRequestForQuotationAgent: build.query<
      RequestForQuotationAndSummaryResponse,
      { requestForQuotationId: string | number }
    >({
      query: ({ requestForQuotationId }) => ({
        url: `/secured/request-for-quotation/${requestForQuotationId}/edit`,
        method: 'GET'
      }),
      transformResponse: (res: RequestForQuotationAndSummaryResponse) => {
        const quotations = res.quotations.map((q) => ({
          ...q,
          husbandryExpensesSection: {
            ...(q.husbandryExpensesSection ?? {}),
            expenses: (q.husbandryExpensesSection?.expenses ?? [])
              .slice()
              .sort((a, b) => {
                if (a.expenseGroupOrder !== b.expenseGroupOrder) {
                  return a.expenseGroupOrder - b.expenseGroupOrder;
                }
                return a.expenseOrder - b.expenseOrder;
              })
              .map((e) => ({
                ...e,
                currency: e.currency ?? res.currency
              }))
          },
          portExpensesSection: {
            ...(q.portExpensesSection ?? {}),
            expenses: (q.portExpensesSection?.expenses ?? []).map((e) => ({
              ...e,
              currency: e.currency ?? res.currency
            }))
          },
          agencyExpensesSection: {
            ...(q.agencyExpensesSection ?? {}),
            expenses: (q.agencyExpensesSection?.expenses ?? []).map((e) => ({
              ...e,
              currency: e.currency ?? res.currency
            }))
          }
        }));

        return { ...res, quotations };
      },
      providesTags: (res, _error, { requestForQuotationId }) =>
        res
          ? [
              {
                type: 'REQUEST_FOR_QUOTATION',
                id: requestForQuotationId.toString()
              }
            ]
          : ['REQUEST_FOR_QUOTATION']
    }),
    markRequestForQuotationAsSeen: build.mutation<
      RequestForQuotationAndSummaryResponse,
      { requestForQuotationId: string | number }
    >({
      query: ({ requestForQuotationId }) => ({
        url: `/secured/request-for-quotation/mark-as-seen`,
        method: 'POST',
        data: requestForQuotationId
      })
    }),
    requestCaseExpensesInclusion: build.mutation<
      RequestCaseQuotationsEvaluationResponse,
      {
        quotationIds: string[] | number[];
        requestCaseId: string | number;
        expensesStatus: 'INCLUDE' | 'EXCLUDE';
      }
    >({
      query: ({ quotationIds, expensesStatus }) => ({
        url: `/secured/request-case/expenses/${expensesStatus}`,
        method: 'POST',
        data: { quotationExpenseIds: quotationIds }
      }),
      invalidatesTags: (res, _error, { requestCaseId }) =>
        res
          ? [{ type: 'REQUEST_CASE_EVALUATE', requestCaseId }]
          : ['REQUEST_CASE_EVALUATE']
    }),
    quoteRequestForQuotation: build.mutation<
      RequestForQuotationAndSummaryResponse,
      QuotationForm
    >({
      query: (data) => ({
        url: `/secured/request-for-quotation/quote`,
        method: 'POST',
        data: data
      }),
      invalidatesTags: (res, _error, { requestForQuotationId }) =>
        res
          ? [{ type: 'REQUEST_FOR_QUOTATION', id: requestForQuotationId }]
          : ['REQUEST_FOR_QUOTATION']
    }),
    declineRequestForQuotation: build.mutation<
      RequestForQuotationAndSummaryResponse,
      string | number
    >({
      query: (data) => ({
        url: `/secured/request-for-quotation/decline`,
        method: 'POST',
        data
      })
    }),
    evaluateRequestCase: build.query<
      RequestCaseQuotationsEvaluationResponse,
      { requestCaseId: string }
    >({
      query: ({ requestCaseId }) => ({
        method: 'GET',
        url: `/secured/request-case/${requestCaseId}/evaluate`
      }),
      transformResponse(res: RequestCaseQuotationsEvaluationResponse) {
        const portExpenses = (res.portExpenses ?? [])
          .slice()
          .map((e) => ({
            ...e,
            expenseGroupName: 'Port Expenses'
          }))
          .sort((a, b) => {
            return a.expenseOrder - b.expenseOrder;
          });

        const agencyExpenses = (res.agencyExpenses ?? [])
          .slice()
          .map((e) => ({
            ...e,
            expenseGroupName: 'Agency Expenses'
          }))
          .sort((a, b) => {
            return a.expenseOrder - b.expenseOrder;
          });

        const husbandryExpenses = (res.husbandryExpenses ?? [])
          .slice()
          .sort((a, b) => {
            if (a.expenseGroupOrder !== b.expenseGroupOrder) {
              return a.expenseGroupOrder - b.expenseGroupOrder;
            }
            return a.expenseOrder - b.expenseOrder;
          });

        const quotations = (res.quotations ?? []).map((q) => ({
          ...q,
          husbandryExpensesSection: {
            ...q.husbandryExpensesSection,
            expenses: (q.husbandryExpensesSection.expenses ?? [])
              .slice()
              .sort((a, b) => {
                if (a.expenseGroupOrder !== b.expenseGroupOrder) {
                  return a.expenseGroupOrder - b.expenseGroupOrder;
                }
                return a.expenseOrder - b.expenseOrder;
              })
              .map((e) => ({
                ...e,
                currency: e.currency || res.currency
              }))
          },
          portExpensesSection: {
            ...q.portExpensesSection,
            expenses: (q.portExpensesSection.expenses ?? []).map((e) => ({
              ...e,
              currency: e.currency || res.currency
            }))
          },
          agencyExpensesSection: {
            ...q.agencyExpensesSection,
            expenses: (q.agencyExpensesSection.expenses ?? []).map((e) => ({
              ...e,
              currency: e.currency || res.currency
            }))
          }
        }));
        return {
          ...res,
          quotations,
          husbandryExpenses,
          portExpenses,
          agencyExpenses
        };
      },
      providesTags: (res, _error, { requestCaseId }) =>
        res
          ? [{ type: 'REQUEST_CASE_EVALUATE', id: requestCaseId }]
          : ['REQUEST_CASE_EVALUATE']
    }),
    shareRequestCase: build.mutation<
      void,
      { requestCaseId: number; emails: string[] }
    >({
      query: ({ requestCaseId, emails }) => ({
        method: 'POST',
        url: `/secured/request-case/${requestCaseId}/share`,
        data: { emails }
      })
    }),
    addMoreAgents: build.mutation<void, RequestsAdditionalAgentsForm>({
      query: (data: RequestsAdditionalAgentsForm) => ({
        method: 'POST',
        url: '/secured/request-case/submit-additional-request-for-quotation',
        data
      }),
      invalidatesTags: (res, _error, { id }) =>
        res
          ? [{ type: 'REQUEST_CASE_EVALUATE', id }]
          : ['REQUEST_CASE_EVALUATE']
    }),
    saveAdditionalExpenses: build.mutation<
      { id: string | number },
      { requestCaseId: string | number; data: RequestsActivitiesExpensesForm }
    >({
      query: (payload) => ({
        method: 'PUT',
        url: `/secured/request-case/${payload.requestCaseId}/additional-expenses`,
        data: payload.data
      }),
      invalidatesTags: (res, _error, { requestCaseId }) =>
        res
          ? [{ type: 'REQUEST_CASE_EVALUATE', id: requestCaseId }]
          : ['REQUEST_CASE_EVALUATE']
    }),
    loadRequestForQuotationExpenses: build.query<
      Array<QuotationExpenseResponse & { pricePerUnit: number }>,
      { requestForQuotationId: string | number }
    >({
      query: ({ requestForQuotationId }) => ({
        method: 'GET',
        url: `/secured/request-for-quotations/${requestForQuotationId}/expenses`
      }),
      providesTags: (res, _error, { requestForQuotationId }) =>
        res
          ? [
              {
                type: 'REQUEST_FOR_QUOTATION_EXPENSES',
                id: requestForQuotationId.toString()
              }
            ]
          : ['REQUEST_FOR_QUOTATION_EXPENSES'],
      transformResponse: (res: QuotationExpenseResponse[]) => {
        return res.map((expense) => ({
          ...expense,
          key: createQuotationExpenseKey(expense),
          pricePerUnit: divide(expense.amount ?? 0, expense.unit ?? 1)
        }));
      }
    }),
    loadPricelistAmountsForRequestForQuotation: build.query<
      RequestForQuotationPricelistAmountsResponse,
      { requestForQuotationId: string | number }
    >({
      query: ({ requestForQuotationId }) => ({
        method: 'GET',
        url: `/secured/request-for-quotations/${requestForQuotationId}/pricelist-amounts`
      }),
      providesTags: (res, _error, { requestForQuotationId }) =>
        res
          ? [
              {
                type: 'REQUEST_FOR_QUOTATION_PRICELIST_AMOUNTS',
                id: requestForQuotationId.toString()
              }
            ]
          : ['REQUEST_FOR_QUOTATION_PRICELIST_AMOUNTS'],
      transformErrorResponse: (error) => {
        return isBaseQueryError(error)
          ? error.data.message
          : 'Error while fetching/updating port call details';
      }
    })
  }),
  overrideExisting: true
});

export const invalidateRequestCase = (requestCaseId: string | number) =>
  requestCaseApi.util.invalidateTags([
    { type: 'REQUEST_CASE', id: requestCaseId.toString() }
  ]);

export const invalidateRequestCaseEvaluation = (
  requestCaseId: string | number
) =>
  requestCaseApi.util.invalidateTags([
    { type: 'REQUEST_CASE_EVALUATE', id: requestCaseId.toString() }
  ]);

export const {
  useLazyLoadRequestCaseQuery,
  useCancelRequestCaseMutation,
  useLoadRequestForQuotationQuery,
  useLoadRequestCaseQuery,
  useSendRequestForQuotationReminderMutation,
  useCancelRequestForQuotationMutation,
  useRenegotiateQuotationMutation,
  useLoadRequestForQuotationAgentQuery,
  useMarkRequestForQuotationAsSeenMutation,
  useQuoteRequestForQuotationMutation,
  useDeclineRequestForQuotationMutation,
  useEvaluateRequestCaseQuery,
  useShareRequestCaseMutation,
  useAddMoreAgentsMutation,
  useSaveAdditionalExpensesMutation,
  useLoadRequestForQuotationExpensesQuery,
  useLoadPricelistAmountsForRequestForQuotationQuery,
  useRequestCaseExpensesInclusionMutation
} = requestCaseApi;

export const addMoreServicesCompletedMatcher =
  requestCaseApi.endpoints['saveAdditionalExpenses'].matchFulfilled;
